import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material'
import { FiFile, FiImage, FiFileText, FiX } from 'react-icons/fi'

const FilePreviewEnhanced = ({ selectedFiles, onRemoveFile }) => {
  const [filePreviews, setFilePreviews] = useState([])
  const [openPreview, setOpenPreview] = useState(null) // store { type, src, name, shouldRevoke }

  const getFileType = file => {
    const ext = file.name.split('.').pop().toLowerCase()
    if (['pdf'].includes(ext)) return 'pdf'
    if (['doc', 'docx'].includes(ext)) return 'document'
    if (['txt', 'md'].includes(ext)) return 'text'
    if (['csv', 'xls', 'xlsx'].includes(ext)) return 'spreadsheet'
    if (['json', 'xml'].includes(ext)) return 'code'
    return 'file'
  }

  const getFileIcon = type => {
    const iconProps = { size: 20, color: '#999' }
    switch (type) {
      case 'image':
        return <FiImage {...iconProps} />
      case 'pdf':
        return <FiFileText {...iconProps} />
      case 'document':
      case 'text':
        return <FiFileText {...iconProps} />
      default:
        return <FiFile {...iconProps} />
    }
  }

  const formatFileSize = bytes => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  useEffect(() => {
    const loadPreviews = async () => {
      const previews = await Promise.all(
        selectedFiles.map(async file => {
          if (file.type.startsWith('image/')) {
            const preview = await new Promise(resolve => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result)
              reader.readAsDataURL(file)
            })
            return { file, preview, type: 'image' }
          }

          const type = getFileType(file)
          return { file, preview: null, type }
        })
      )
      setFilePreviews(previews)
    }

    loadPreviews()
    return () => setFilePreviews([])
  }, [selectedFiles])

  // Handle modal preview open
  const handleOpenPreview = fileObj => {
    if (fileObj.type === 'pdf' || fileObj.type === 'image') {
      const shouldRevoke = !fileObj.preview
      const src = fileObj.preview || URL.createObjectURL(fileObj.file)
      setOpenPreview(prev => {
        if (prev?.shouldRevoke && prev.src) {
          URL.revokeObjectURL(prev.src)
        }
        return {
          type: fileObj.type,
          src,
          name: fileObj.file.name,
          shouldRevoke,
        }
      })
    }
  }

  const handleClosePreview = () =>
    setOpenPreview(prev => {
      if (prev?.shouldRevoke && prev.src) {
        URL.revokeObjectURL(prev.src)
      }
      return null
    })

  useEffect(() => {
    return () => {
      if (openPreview?.shouldRevoke && openPreview.src) {
        URL.revokeObjectURL(openPreview.src)
      }
    }
  }, [openPreview])

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
          borderRadius: 1,
          mb: 1,
        }}
      >
        {filePreviews.map((item, idx) => (
          <Card
            key={idx}
            sx={{
              position: 'relative',
              width: 100,
              cursor:
                item.type === 'image' || item.type === 'pdf' ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3,
              },
            }}
            onClick={() => handleOpenPreview(item)}
          >
            <Tooltip title="Remove file">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  onRemoveFile(idx)
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  zIndex: 10,
                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                }}
              >
                <FiX size={16} />
              </IconButton>
            </Tooltip>

            {/* Thumbnail preview */}
            {item.type === 'image' && item.preview ? (
              <CardMedia
                component="img"
                height="80"
                image={item.preview}
                alt={item.file.name}
                sx={{ objectFit: 'contain', bgcolor: 'grey.50' }}
              />
            ) : item.type === 'pdf' ? (
              <Box
                sx={{
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                  flexDirection: 'column',
                }}
              >
                <FiFileText size={28} color="#999" />
                <Typography variant="caption" color="text.secondary" noWrap>
                  PDF
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                {getFileIcon(item.type)}
              </Box>
            )}

            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Tooltip title={item.file.name}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.file.name.length > 20
                    ? `${item.file.name.substring(0, 17)}...`
                    : item.file.name}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(item.file.size)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Dialog open={!!openPreview} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogContent
          sx={{
            position: 'relative',
            p: 0,
            bgcolor: 'black',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '90vh',
          }}
        >
          <IconButton
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.8)',
              zIndex: 100,
              '&:hover': { bgcolor: 'white' },
            }}
          >
            <FiX size={20} />
          </IconButton>

          {openPreview &&
            (openPreview.type === 'image' ? (
              <img
                src={openPreview.src}
                alt={openPreview.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <embed
                src={openPreview.src}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  objectFit: 'contain',
                  minHeight: '90vh',
                }}
              />
            ))}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default FilePreviewEnhanced
